<svelte:options runes={true} />

<script lang="ts">
  import { untrack } from "svelte";
  import Button from "~/components/ui/Button/Button.svelte";
  import IconSearch from "~icons/dusk/search";
  import DesktopSearchShell from "~/components/search/DesktopSearchShell/DesktopSearchShell.svelte";
  import { createSearchAutosuggestCore } from "~/lib/search/create-search-autosuggest-core.svelte";
  import { createIsDesktop } from "~/lib/create-is-desktop.svelte";
  import { searchUrl } from "~/lib/url-state";
  import type { Autosuggest } from "~/lib/listings/create-autosuggest.svelte";

  interface Props {
    accountId?: string;
    domainKey?: string;
    authKey?: string;
    /** Whatever was already in the pre-hydration skeleton's field at mount — see the entrypoint. */
    initialQuery?: string;
    /**
     * Stories and tests only — Bloomreach is unreachable from Storybook, so the
     * panel can only be shown by injecting one of the fixtures. Production leaves
     * this unset and the core builds a real `createAutosuggest(config)`.
     */
    autosuggest?: Autosuggest;
  }

  let { accountId, domainKey, authKey, initialQuery, autosuggest }: Props = $props();

  const core = createSearchAutosuggestCore({
    config: {
      get accountId() {
        return accountId ?? "";
      },
      get domainKey() {
        return domainKey ?? "";
      },
      get authKey() {
        return authKey;
      },
    },
    autosuggest: untrack(() => autosuggest),
  });

  // Seeding is deliberately one-shot: this prop describes the state the island was
  // handed at mount, and the core owns the query from here on. Never opens the panel —
  // ponytail: a visitor who beat the mount with a couple of keystrokes waits one more
  // keystroke for the panel to open, same as any other island's hydration race.
  untrack(() => {
    if (initialQuery) core.seedDisplayValue(initialQuery);
  });

  const isDesktop = createIsDesktop();

  let mobileDialogOpen = $state(false);

  function submit(query: string) {
    window.location.href = searchUrl(query);
  }

  // A chunk that fails to load (network, or a stale hash after a deploy) must not leave
  // the trigger inert: fall back to the plain search page, which works without the sheet.
  let mobileShellFailed = $state(false);

  function loadMobileShell() {
    return import("~/components/search/MobileSearchShell/MobileSearchShell.svelte").catch(
      (error) => {
        console.error("[Aurora] Failed to load the mobile search sheet:", error);
        mobileShellFailed = true;
        if (mobileDialogOpen) submit("");
        throw error;
      },
    );
  }
</script>

<!-- One shell at a time, chosen by matchMedia rather than hidden with CSS: two mounted
     shells means two comboboxes on the page, and the hidden one would still be measuring
     a zero-width box to decide how many cards fit. -->
<div class="da-search-autosuggest-island">
  {#if isDesktop.current}
    <DesktopSearchShell {core} onSubmit={submit} />
  {:else}
    <Button
      variant="tertiary"
      color="charcoal"
      size="md"
      iconOnly
      class="da-search-autosuggest-island__trigger"
      aria-label="Open search"
      aria-haspopup="dialog"
      data-test-element="mobile-search"
      onclick={() => {
        if (mobileShellFailed) submit("");
        else mobileDialogOpen = true;
      }}
    >
      {#snippet leadingIcon()}
        <IconSearch width="24" height="24" aria-hidden="true" />
      {/snippet}
    </Button>
    <!-- Loaded on demand: the sheet is the only thing in this island that pulls in
         bits-ui's Dialog, and desktop never renders it. Imported at mount rather than
         on the first tap, so the chunk is there before anyone reaches for it. -->
    {#await loadMobileShell() then { default: MobileSearchShell }}
      <MobileSearchShell
        {core}
        open={mobileDialogOpen}
        onOpenChange={(open) => (mobileDialogOpen = open)}
        onSubmit={submit}
      />
    {:catch}
      <!-- Already logged and handled by loadMobileShell. -->
    {/await}
  {/if}
</div>

<style>
  @layer aurora {
    .da-search-autosuggest-island {
      width: 100%;
    }

    /* Sized to the theme icon this replaces (`.header__icon`), not to Button's default
       40x40, or the swap shifts the header's icon row once Aurora mounts.

       The theme's tokens rather than hard px, because a merchant can rescale the header
       via `--font-body-scale`. The px fallbacks are the intended size, not the theme's
       3.2rem/4.8rem — those only mean 32/48 under the theme's 62.5% root, which Aurora's
       own stylesheet does not set.

       Three classes so this outweighs Button's own `.da-button--icon-only.da-button--md`
       without naming the size variant. `:global` because the class travels through
       Button's `class` prop, as Button's own rules do. */
    :global(.da-button.da-button--icon-only.da-search-autosuggest-island__trigger) {
      width: var(--layout-space-16, 32px);
      height: var(--layout-space-24, 48px);
    }
  }
</style>
